import { getDocument } from '@atlaskit/browser-apis';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { Spotlight, SpotlightTransition } from '@atlaskit/onboarding';
import { getGlobalTheme } from '@atlaskit/tokens';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
// @ts-expect-error: 'React' is declared but its value is never read
// React import required to render in Jira SSR
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { spotlightMessages } from './messages';
import ListMergeSpotlightIllustrationDark from '../assets/list-merge-spotlight-dark.gif';
import ListMergeSpotlightIllustration from '../assets/list-merge-spotlight-light.gif';

export const TELL_ME_MORE_URL =
	'https://community.atlassian.com/forums/Jira-articles/We-re-merging-List-and-All-work-one-powerful-view-for-all-teams/ba-p/3090105';

type ListMergeSpotlightTourProps = {
	/**
		Expose prop to hide the illustration for VR testing
		**/
	withIllustration?: boolean;
};

export const ListMergeSpotlightTour = ({
	withIllustration = true,
}: ListMergeSpotlightTourProps) => {
	const [isActive, setIsActive] = useState(true);
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const isDarkMode = getGlobalTheme().colorMode === 'dark';
	const Illustration = isDarkMode
		? ListMergeSpotlightIllustrationDark
		: ListMergeSpotlightIllustration;

	const { formatMessage } = useIntl();

	const handleDismiss = useCallback(() => {
		setIsActive(false);
		void messageInstanceClicked({
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {},
			clickedElement: {
				buttonType: 'secondary',
				clickedElement: 'button',
				clickIdentifier: 'dismiss',
			},
			messageEventType: 'clicked',
		});
	}, [messageInstanceClicked]);

	const handleTellMeMore = useCallback(() => {
		// eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-all-member-expressions
		window.open(TELL_ME_MORE_URL);
		handleDismiss();
	}, [handleDismiss]);

	useEffect(() => {
		const handleEscape = (ev: KeyboardEvent) => {
			if (ev.key === 'Escape') {
				handleDismiss();
			}
		};

		getDocument()?.addEventListener('keydown', handleEscape);

		return () => {
			getDocument()?.removeEventListener('keydown', handleEscape);
		};
	}, [handleDismiss]);

	return (
		isActive && (
			<SpotlightTransition>
				<Spotlight
					actions={[
						{
							onClick: handleTellMeMore,
							text: formatMessage(spotlightMessages.spotlightTellMeMore),
							'aria-label': formatMessage(spotlightMessages.tellMeMoreAriaLabel),
						},
						{
							onClick: handleDismiss,
							text: formatMessage(spotlightMessages.spotlightDismiss),
							appearance: 'subtle',
						},
					]}
					heading={formatMessage(spotlightMessages.spotlightHeading)}
					target="horizontal-nav-tabs.LIST"
					key="horizontal-nav-tabs.LIST"
					targetRadius={3}
					targetBgColor={'var(--project-background-input)'}
					dialogWidth={275}
					image={withIllustration ? Illustration : undefined}
					pulse={true}
				>
					{formatMessage(spotlightMessages.spotlightBody)}
				</Spotlight>
			</SpotlightTransition>
		)
	);
};

export default ListMergeSpotlightTour;
